import React, { useEffect, useId, useState } from 'react';
import { ChevronLeft, ChevronRight, Diamond } from 'lucide-react';
import { ANIMATION_INTERPOLATIONS, ANIMATION_PRESETS, ANIMATION_PROPERTIES, ANIMATION_TIME_LIMIT, animationClock, clipAnimationTime, evaluateTransform, KEYFRAME_EPSILON, keyframeAbsoluteTime, MAX_CLIP_KEYFRAMES, presetAnimation, removeKeyframe, setKeyframe, visibleKeyframes } from '../../lib/creatorAnimation';

const styles = {
  root: { display: 'grid', gap: 12, minWidth: 0, maxWidth: '100%', fontSize: 11 },
  row: { display: 'grid', gap: 5, minWidth: 0, paddingBottom: 9, borderBottom: '1px solid #303a42' },
  heading: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0, color: '#bccbd1' },
  values: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 5, alignItems: 'center', minWidth: 0 },
  actions: { display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 },
  input: { width: '100%', minWidth: 0, minHeight: 32, padding: '5px 6px', border: '1px solid #44515a', borderRadius: 4, background: '#0b1015', color: '#e7edf1', fontSize: 11 },
  button: { width: 26, height: 30, padding: 0, flex: '0 0 26px' },
  count: { color: '#93a6b0', fontVariantNumeric: 'tabular-nums' },
};

function Tool({ label, title = label, children, active = false, ...props }) {
  return <button type="button" className={`creator-icon ${active ? 'is-active' : ''}`} style={styles.button} aria-label={label} title={title} {...props}>{children}</button>;
}

const keyCount = clip => Array.isArray(clip.keyframes) ? clip.keyframes.length : 0;

export default function CreatorAnimationControls({ clip, time = 0, onChange, onSeek, disabled = false, keyframeBudget = MAX_CLIP_KEYFRAMES }) {
  const id = useId();
  const [status, setStatus] = useState('');
  useEffect(() => setStatus(''), [clip?.id, keyframeBudget]);
  if (!clip) return null;
  const currentTime = clipAnimationTime(clip, time);
  const clock = animationClock(clip, currentTime);
  const transform = evaluateTransform(clip, currentTime);
  const keys = Array.isArray(clip.keyframes) ? clip.keyframes : [];
  const visible = visibleKeyframes(clip);
  const locked = disabled || typeof onChange !== 'function';
  const validClock = Number.isFinite(clock) && Math.abs(clock) <= ANIMATION_TIME_LIMIT;
  const budget = Number.isFinite(keyframeBudget) ? Math.max(0, Math.min(MAX_CLIP_KEYFRAMES, Math.floor(keyframeBudget))) : MAX_CLIP_KEYFRAMES;
  const full = keys.length >= budget;
  const limitTitle = budget === MAX_CLIP_KEYFRAMES ? 'Limite de 60 keyframes por clip' : `Presupuesto disponible: ${budget} keyframes para este clip (limite de 800 por proyecto)`;
  const clockTitle = 'El tiempo de animacion supera el limite permitido';
  const exceedsBudget = (current, next) => keyCount(next) > budget && keyCount(next) > keyCount(current);
  const presets = ANIMATION_PRESETS.map(option => {
    const next = presetAnimation(clip, option.id);
    const blocked = exceedsBudget(clip, next);
    return { ...option, available: next !== clip && !blocked, reason: blocked || full ? limitTitle : clockTitle };
  });
  const change = (updater, label) => {
    if (locked) return;
    const next = updater(clip);
    if (exceedsBudget(clip, next)) { setStatus(limitTitle); return; }
    if (next === clip) return;
    setStatus('');
    onChange(current => {
      const updated = updater(current);
      return exceedsBudget(current, updated) ? current : updated;
    }, label);
  };

  return <div className="creator-animation-controls" data-testid="creator-animation-controls" style={styles.root} onKeyDown={event => event.stopPropagation()}>
    <label className="creator-field" style={{ marginBottom: 0 }}><span>Preset<output aria-label="Keyframes visibles" style={styles.count}>{visible.length}</output></span>
      <select aria-label="Preset de animacion" value="" disabled={locked || !presets.some(option => option.available)}
        title={presets.some(option => option.available) ? 'Preset de animacion' : full ? limitTitle : clockTitle}
        onChange={event => {
          const value = event.target.value;
          const option = presets.find(item => item.id === value);
          if (option?.available) change(current => presetAnimation(current, value), `Animacion: ${option.label}`);
          else if (option) setStatus(option.reason);
        }}>
        <option value="">Seleccionar animacion</option>
        {presets.map(option => <option value={option.id} key={option.id} disabled={!option.available} title={option.available ? option.label : option.reason}>{option.label}</option>)}
      </select>
    </label>
    {ANIMATION_PROPERTIES.map(item => {
      const propertyKeys = visible.filter(key => key.property === item.property);
      const atTime = propertyKeys.find(key => Math.abs(key.time - clock) <= KEYFRAME_EPSILON);
      const previous = propertyKeys.filter(key => key.time < clock - KEYFRAME_EPSILON).at(-1);
      const next = propertyKeys.find(key => key.time > clock + KEYFRAME_EPSILON);
      const interpolation = atTime?.interpolation || previous?.interpolation || 'linear';
      const cannotAdd = locked || !validClock || full && !atTime;
      const addLabel = `${atTime ? 'Eliminar' : 'Agregar'} keyframe de ${item.label}`;
      return <div style={styles.row} key={item.property} data-testid={`creator-keyframe-row-${item.property}`}>
        <div style={styles.heading}><label htmlFor={`${id}-${item.property}`}>{item.label}</label><output aria-label={`Keyframes visibles de ${item.label}`} style={styles.count}>{propertyKeys.length}</output></div>
        <div style={styles.values}>
          <input id={`${id}-${item.property}`} aria-label={`Keyframe ${item.label}`} type="number" min={item.min} max={item.max} step={item.step}
            style={styles.input} value={Number(transform[item.property].toFixed(6))} disabled={cannotAdd}
            title={!validClock ? clockTitle : full && !atTime ? limitTitle : `Keyframe ${item.label}`}
            onChange={event => {
              const value = event.target.valueAsNumber;
              if (Number.isFinite(value)) change(current => setKeyframe(current, item.property, currentTime, value, interpolation), `Keyframe: ${item.label}`);
            }} />
          <div style={styles.actions}>
            <Tool label={`Keyframe anterior de ${item.label}`} disabled={disabled || !onSeek || !previous}
              onClick={() => onSeek(clipAnimationTime(clip, keyframeAbsoluteTime(clip, previous.time)))}><ChevronLeft size={14} aria-hidden="true" /></Tool>
            <Tool label={addLabel} title={atTime ? addLabel : !validClock ? clockTitle : full ? limitTitle : addLabel} active={Boolean(atTime)} aria-pressed={Boolean(atTime)} disabled={atTime ? locked : cannotAdd}
              onClick={() => change(current => atTime ? removeKeyframe(current, item.property, currentTime)
                : setKeyframe(current, item.property, currentTime, transform[item.property], interpolation), `${atTime ? 'Eliminar' : 'Agregar'} keyframe: ${item.label}`)}>
              <Diamond size={13} fill={atTime ? 'currentColor' : 'none'} aria-hidden="true" />
            </Tool>
            <Tool label={`Keyframe siguiente de ${item.label}`} disabled={disabled || !onSeek || !next}
              onClick={() => onSeek(clipAnimationTime(clip, keyframeAbsoluteTime(clip, next.time)))}><ChevronRight size={14} aria-hidden="true" /></Tool>
          </div>
        </div>
        <select aria-label={`Interpolacion de ${item.label}`} title={!validClock ? clockTitle : full && !atTime ? limitTitle : `Interpolacion de ${item.label}`}
          style={styles.input} value={interpolation} disabled={cannotAdd} onChange={event => {
            const value = event.target.value;
            change(current => setKeyframe(current, item.property, currentTime, transform[item.property], value), `Interpolacion: ${item.label}`);
          }}>{ANIMATION_INTERPOLATIONS.map(option => <option value={option.id} key={option.id}>{option.label}</option>)}</select>
      </div>;
    })}
    {(status || full) && <p role="status" style={{ margin: 0, color: '#e6c493', overflowWrap: 'anywhere' }}>{status || limitTitle}</p>}
  </div>;
}
